import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { ClawInstallMarketingShell } from "./ClawInstallMarketingShell.tsx";

/** Routes of the three ClawInstall legal documents, used for cross-linking. */
const legalDocuments = [
  { to: "/clawinstall/terms", label: "Terms of Service" },
  { to: "/clawinstall/privacy", label: "Privacy Policy" },
  { to: "/clawinstall/acceptable-use", label: "Acceptable Use Policy" },
] as const;

/** Route path of a ClawInstall legal document. */
export type LegalDocumentPath = (typeof legalDocuments)[number]["to"];

/** Inline link styling shared by the legal pages (blue, design-board look). */
export const legalLinkClass =
  "text-secondary underline decoration-outline-variant underline-offset-4 transition-colors hover:text-on-surface";

/** Shared bulleted-list styling for the legal pages. */
export const legalListClass = "list-disc space-y-2 pl-6 marker:text-secondary";

/** Shared styling for numbered subsection sub-headings (e.g. "2.1 …"). */
export const legalSubheadingClass = "font-headline-md text-body-lg font-semibold tracking-tight text-on-surface";

/**
 * A numbered section of a legal document: heading plus prose content.
 *
 * @since 1.0.0
 */
export function LegalSection({ children, title }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="scroll-mt-24 font-headline-md text-headline-md tracking-tighter text-on-surface">{title}</h2>
      {children}
    </section>
  );
}

/**
 * Shared layout for the ClawInstall legal pages (Terms, Privacy, AUP):
 * marketing shell, a "Legal" kicker, document title, version + updated badges,
 * a panel-chrome article, sectioned prose, and cross-links to the other legal
 * documents rendered as ghost buttons.
 *
 * @since 1.0.0
 */
export function LegalPageLayout({ children, current, lastUpdated, testId, title, version }: {
  readonly title: string;
  readonly lastUpdated: string;
  readonly version: string;
  readonly current: LegalDocumentPath;
  readonly testId: string;
  readonly children: ReactNode;
}) {
  return (
    <ClawInstallMarketingShell>
      <div data-testid={testId} className="bg-surface py-20 sm:py-28">
        <article className="mx-auto max-w-3xl rounded-2xl border border-outline-variant bg-surface-container-lowest p-8 shadow-[0_16px_34px_rgba(31,75,108,0.08)] md:p-12">
          <p className="font-sans text-[10px] font-bold uppercase tracking-[0.16em] text-secondary">Legal</p>
          <h1 className="mt-3 font-headline-lg text-headline-lg tracking-tighter text-on-surface">{title}</h1>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-secondary-container px-2 py-1 font-sans text-[10px] font-bold uppercase tracking-[0.06em] text-on-secondary-container">
              Version {version}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-tertiary-container px-2 py-1 font-sans text-[10px] font-bold uppercase tracking-[0.06em] text-tertiary">
              Updated {lastUpdated}
            </span>
          </div>
          <div className="mt-12 space-y-12 font-sans text-body-md leading-7 text-on-surface-variant">
            {children}
          </div>
          <nav
            aria-label="Related policies"
            className="mt-16 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-outline-variant/60 pt-8"
          >
            <span className="font-label-md text-label-md text-on-surface">Related policies:</span>
            {legalDocuments.filter((doc) => doc.to !== current).map((doc) => (
              <Link
                key={doc.to}
                to={doc.to}
                className="rounded-lg border border-outline-variant bg-surface-container-lowest px-4 py-2.5 font-sans text-sm font-bold text-on-surface transition-colors hover:bg-surface-container"
              >
                {doc.label}
              </Link>
            ))}
          </nav>
        </article>
      </div>
    </ClawInstallMarketingShell>
  );
}
